import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Percent, CheckCircle2, Ban, Loader2 } from 'lucide-react';
import { ReconciliationIssueRecord } from '@/types/database';
import { reconciliationService } from '@/services/reconciliationService';
import { supabase } from '@/lib/supabaseClient';

interface RateRuleModalProps {
  issue: ReconciliationIssueRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const RateRuleModal: React.FC<RateRuleModalProps> = ({
  issue,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [decision, setDecision] = useState<'activate' | 'keep_draft'>('activate');
  const [rateValue, setRateValue] = useState<number>(0);
  const [rateUnit, setRateUnit] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [ruleRecord, setRuleRecord] = useState<any>(null);
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!issue || !isOpen) return;

    setError(null);
    if (issue.entity_id) {
      supabase
        .from('rate_rules')
        .select('*')
        .eq('id', issue.entity_id)
        .single()
        .then(({ data }) => {
          if (data) {
            setRuleRecord(data);
            setRateValue(Number(data.rate_value) || 0);
            setRateUnit(data.rate_unit || '');
            setStartDate(data.effective_start_date || '2026-01-01');
            setEndDate(data.effective_end_date || '');
          }
        });
    }
  }, [issue, isOpen]);

  const handleSubmit = async () => {
    if (!issue) return;
    setError(null);
    setSubmitting(true);

    try {
      await reconciliationService.resolveRateRule(
        issue.id,
        {
          decision,
          rule_id: issue.entity_id || ruleRecord?.id,
          rate_value: rateValue,
          rate_unit: rateUnit,
          effective_start_date: startDate || null,
          effective_end_date: endDate || null,
          is_active: decision === 'activate',
          notes: adminNotes,
        },
        adminNotes
      );

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error resolving rate rule:', err);
      setError(err.message || 'Failed to update rate rule');
    } finally {
      setSubmitting(false);
    }
  };

  if (!issue) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
              <Percent className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-slate-900">
                Rate Rule Review & Activation
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Verify rate rule applicability and activate as canonical production rule.
              </p>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Rule Diagnostic */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">
              Yellow Rule Evidence
            </span>
            <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-amber-100 text-amber-800 font-bold">
              Draft / Needs Review
            </span>
          </div>
          <div>
            <span className="text-slate-500">Rule String:</span>
            <div className="font-mono font-bold text-slate-900 mt-0.5">{issue.legacy_value}</div>
          </div>
          <div>
            <span className="text-slate-500">Evidence Notes:</span>
            <div className="text-slate-700 mt-0.5">{issue.description}</div>
          </div>
        </div>

        {/* Decision Toggle */}
        <div className="space-y-3">
          <Label className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            Resolution Action:
          </Label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div
              onClick={() => setDecision('activate')}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                decision === 'activate'
                  ? 'border-emerald-600 bg-emerald-50/60 ring-1 ring-emerald-600'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="rate_decision"
                  checked={decision === 'activate'}
                  onChange={() => setDecision('activate')}
                  className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    Activate as Production Rule
                  </span>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Sets rule to active status for settlements and sales calculations.
                  </p>
                </div>
              </div>
            </div>

            <div
              onClick={() => setDecision('keep_draft')}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                decision === 'keep_draft'
                  ? 'border-slate-600 bg-slate-100 ring-1 ring-slate-600'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="rate_decision"
                  checked={decision === 'keep_draft'}
                  onChange={() => setDecision('keep_draft')}
                  className="mt-0.5 text-slate-600 focus:ring-slate-500"
                />
                <div>
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1">
                    <Ban className="w-3.5 h-3.5 text-slate-500" />
                    Keep Inactive Draft
                  </span>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Retains rule in database as inactive draft.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {decision === 'activate' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs">
              <div>
                <Label className="text-[11px] font-semibold text-slate-700">Rate Value {rateUnit ? `(${rateUnit.replace('PHP', '₱')})` : '(₱)'}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rateValue}
                  onChange={(e) => setRateValue(parseFloat(e.target.value) || 0)}
                  className="mt-1 text-xs h-8"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold text-slate-700">Effective Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1 text-xs h-8"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold text-slate-700">Effective End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1 text-xs h-8"
                />
              </div>
            </div>
          )}
        </div>

        {/* Resolution Notes */}
        <div>
          <Label className="text-xs font-semibold text-slate-700">Resolution Audit Notes</Label>
          <Input
            type="text"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="e.g. Confirmed shrinkage rate with VSB management"
            className="mt-1 text-xs"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="min-h-[44px] text-xs font-semibold text-slate-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="min-h-[44px] bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 text-xs font-semibold"
          >
            {submitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Updating Rule...</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                <span>Save Rate Rule Decision</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
